import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { cn } from '@/lib/utils';

export const PaginationOptions = [
  { label: '10 per page', value: '10' },
  { label: '25 per page', value: '25' },
  { label: '50 per page', value: '50' },
  { label: '100 per page', value: '100' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type Props = {
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  perPage?: string;
  setPerPage?: (perPage: number) => void;
  maxResults?: number;
};

export const Pagination = ({
  page,
  totalPages,
  perPage = '10',
  setPage,
  setPerPage,
  maxResults,
}: Props) => {
  const perPageNum = Number(perPage) || 10;
  const totalRows = maxResults ?? totalPages * perPageNum;
  const start = (page - 1) * perPageNum + 1;
  const end = Math.min(page * perPageNum, maxResults ?? Infinity);
  const canPrevious = page > 1 && totalPages > 0;
  const canNext = page < totalPages && totalPages > 0;

  const handlePerPageChange = (value: string) => {
    const num = Number(value);
    if (!Number.isNaN(num) && setPerPage) {
      setPerPage(num);
      setPage(1);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-2 py-4 sm:flex-row sm:items-center sm:justify-between w-full',
      )}
    >
      <div className='text-sm text-muted-foreground text-center sm:text-left'>
        <span>
          {totalRows > 0
            ? `${start}-${Number.isFinite(end) ? end : totalRows} of ${totalRows}`
            : '0 total rows'}
        </span>
      </div>

      <div className='flex flex-wrap items-center justify-center gap-4 sm:justify-end sm:gap-6 lg:gap-8'>
        {setPerPage && (
          <div className='hidden items-center space-x-2 sm:flex'>
            <p className='text-sm font-medium whitespace-nowrap'>Rows per page</p>
            <Select value={perPage} onValueChange={handlePerPageChange}>
              <SelectTrigger className='h-8 w-[70px]'>
                <SelectValue placeholder={perPage} />
              </SelectTrigger>
              <SelectContent side='top'>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className='flex items-center justify-center text-sm font-medium whitespace-nowrap'>
          Page {page} of {totalPages || 1}
        </div>

        <div className='flex items-center space-x-1 sm:space-x-2'>
          <Button
            variant='outline'
            className='hidden h-8 w-8 p-0 lg:flex'
            onClick={() => setPage(1)}
            disabled={!canPrevious}
            type='button'
          >
            <span className='sr-only'>Go to first page</span>
            <ChevronsLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            className='h-8 w-8 p-0'
            onClick={() => setPage(page - 1)}
            disabled={!canPrevious}
            type='button'
          >
            <span className='sr-only'>Go to previous page</span>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            className='h-8 w-8 p-0'
            onClick={() => setPage(page + 1)}
            disabled={!canNext}
            type='button'
          >
            <span className='sr-only'>Go to next page</span>
            <ChevronRight className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            className='hidden h-8 w-8 p-0 lg:flex'
            onClick={() => setPage(totalPages)}
            disabled={!canNext}
            type='button'
          >
            <span className='sr-only'>Go to last page</span>
            <ChevronsRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
};
